import { BaseProvider } from "./base.provider";

export interface FeatureFlag {
  key: string;
  description: string;
  rollout_percentage: number;
  created_at: string;
}

export interface FeatureFlagUser {
  UserId: string;
  Email: string;
  Username: string;
  FirstName: string;
  LastName: string;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface UserFeatureOverride {
  user_id: string;
  email: string;
  username: string;
  is_enabled: boolean;
}

export type MissUniverse = "dev" | "staging" | "prod"

export interface FeatureFlagMiss {
  key: string;
  first_seen_at: string;
  miss_count: number;
  universe: MissUniverse;
}

export interface CreateFeatureFlagPayload {
  key: string;
  description: string;
  rollout_percentage: number;
}

export interface UpdateFeatureFlagPayload {
  description: string;
  rollout_percentage: number;
}

class FeatureFlagsProvider extends BaseProvider {
  list(): Promise<FeatureFlag[]> {
    return this.request<FeatureFlag[]>("/v1/features");
  }

  get(key: string): Promise<FeatureFlag> {
    return this.request<FeatureFlag>(`/v1/features/${key}`);
  }

  create(payload: CreateFeatureFlagPayload): Promise<void> {
    return this.request<void>("/v1/features", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  update(key: string, payload: UpdateFeatureFlagPayload): Promise<void> {
    return this.request<void>(`/v1/features/${key}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  delete(key: string): Promise<void> {
    return this.request<void>(`/v1/features/${key}`, { method: "DELETE" });
  }

  listUsers(key: string): Promise<FeatureFlagUser[]> {
    return this.request<FeatureFlagUser[]>(`/v1/features/${key}/users`);
  }

  listOverrides(key: string, page: number = 1): Promise<UserFeatureOverride[]> {
    return this.request<UserFeatureOverride[]>(`/v1/features/${key}/overrides?page=${page}`);
  }

  setUserOverride(key: string, userId: string, isEnabled: boolean): Promise<void> {
    return this.request<void>(`/v1/features/${key}/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify({ isEnabled }),
    });
  }

  removeUserOverride(key: string, userId: string): Promise<void> {
    return this.request<void>(`/v1/features/${key}/users/${userId}`, { method: "DELETE" });
  }

  check(key: string): Promise<{ enabled: boolean }> {
    return this.request<{ enabled: boolean }>(`/v1/features/check/${key}`);
  }

  listMisses(universe?: MissUniverse): Promise<FeatureFlagMiss[]> {
    const qs = universe ? `?universe=${universe}` : ""
    return this.request<FeatureFlagMiss[]>(`/v1/features/misses${qs}`);
  }

  deleteMiss(key: string): Promise<void> {
    return this.request<void>(`/v1/features/misses/${key}`, { method: "DELETE" });
  }
}

export const featureFlagsProvider = new FeatureFlagsProvider();
